import React from 'react'
import { SettingsData } from '../../interfaces/Settings/settings'
import { Box, Typography, Divider } from '@mui/material'
import { SettingLine } from './SettingLine'

interface SettingsListProps {
  settings: SettingsData[]
  forcedDisabledIds?: Set<number>
  conflictingMetaIds?: Set<number>
  pendingValuesByValuesId?: Record<number, string>
  onSettingChange?: (
    settingsValuesId: number,
    newValue: string,
    productOriginId: number | null,
    isDirty: boolean
  ) => void
}

const BoolHeader = ({
  hideConfigLabel = false
}: {
  hideConfigLabel?: boolean
}) => (
  <Box
    display='grid'
    gridTemplateColumns='1fr 100px 100px'
    alignItems='center'
    px={3}
    py={1}
    sx={{
      borderBottom: '1px solid',
      borderColor: 'divider',
      bgcolor: 'grey.50'
    }}
  >
    <Typography variant='caption' fontWeight={500} color='text.secondary'>
      {hideConfigLabel ? '' : 'Configuración'}
    </Typography>
    <Typography
      variant='caption'
      fontWeight={500}
      color='text.secondary'
      textAlign='center'
    >
      Activo
    </Typography>
    <Typography
      variant='caption'
      fontWeight={500}
      color='text.secondary'
      textAlign='center'
    >
      Inactivo
    </Typography>
  </Box>
)

const ValueHeader = ({
  hideConfigLabel = false
}: {
  hideConfigLabel?: boolean
}) => (
  <Box
    display='grid'
    gridTemplateColumns='1fr 200px'
    alignItems='center'
    px={3}
    py={1}
    sx={{
      borderBottom: '1px solid',
      borderColor: 'divider',
      bgcolor: 'grey.50'
    }}
  >
    <Typography variant='caption' fontWeight={500} color='text.secondary'>
      {hideConfigLabel ? '' : 'Configuración'}
    </Typography>
    <Typography
      variant='caption'
      fontWeight={500}
      color='text.secondary'
      textAlign='center'
    >
      Valor
    </Typography>
  </Box>
)

export const SettingsList = ({
  settings,
  forcedDisabledIds,
  conflictingMetaIds,
  pendingValuesByValuesId,
  onSettingChange
}: SettingsListProps) => {
  const boolSettings = settings.filter((s) => s.dataType === 'bool')
  const valueSettings = settings.filter(
    (s) => s.dataType === 'string' || s.dataType === 'number'
  )

  const hasBoth = boolSettings.length > 0 && valueSettings.length > 0

  const renderGroup = (
    group: SettingsData[],
    Header: React.FC<{ hideConfigLabel?: boolean }>,
    hideConfigLabel: boolean
  ) => {
    if (group.length === 0) return null

    return (
      <Box>
        <Header hideConfigLabel={hideConfigLabel} />
        {group.map((setting, index) => (
          <React.Fragment key={setting.settingsMetadataId}>
            <SettingLine
              {...setting}
              forcedValue={
                forcedDisabledIds?.has(setting.settingsMetadataId)
                  ? 'false'
                  : undefined
              }
              isConflict={conflictingMetaIds?.has(setting.settingsMetadataId)}
              pendingValue={pendingValuesByValuesId?.[setting.settingsValuesId]}
              onChange={onSettingChange}
            />
            {index < group.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      {renderGroup(boolSettings, BoolHeader, false)}

      {hasBoth && <Divider sx={{ borderWidth: 2 }} />}

      {renderGroup(valueSettings, ValueHeader, hasBoth)}
    </Box>
  )
}
